import { useTranslation } from 'react-i18next';

// Generic interface for items with localized names
export interface LocalizableItem {
  name?: string;
  name_french?: string;
  name_english?: string;
}

/**
 * Hook to get localized names for categories, subcategories, and fields
 * @param item - The item with localized names
 * @returns The localized name based on current language
 */
export const useLocalizedName = () => {
  const { i18n } = useTranslation();
  
  const getLocalizedName = (item: LocalizableItem): string => {
    const currentLanguage = i18n.language;
    
    // Use API provided localized names
    if (currentLanguage === 'en') {
      return item.name_english || item.name_french || item.name || 'Unknown';
    } else if (currentLanguage === 'fr') {
      return item.name_french || item.name_english || item.name || 'Unknown';
    }
    
    // Fallback to default name
    return item.name || item.name_french || item.name_english || 'Unknown';
  };

  return { getLocalizedName };
};

/**
 * Utility function to get localized name without hook (for use outside components)
 * @param item - The item with localized names
 * @param language - The current language ('en' or 'fr')
 * @returns The localized name based on language
 */
export const getLocalizedName = (item: LocalizableItem, language: string): string => {
  // Use API provided localized names
  if (language === 'en') {
    return item.name_english || item.name_french || item.name || 'Unknown';
  } else if (language === 'fr') {
    return item.name_french || item.name_english || item.name || 'Unknown';
  }
  
  // Fallback to default name
  return item.name || item.name_french || item.name_english || 'Unknown';
};
